import { getLeadNotes, type NoteMutation } from "@/lib/lead-update";
import { finishNoteSave, mergeNoteUpdate, type NoteDraft, type NoteUpdateHandler } from "@/lib/note-ui";
import type { Dispatch, SetStateAction } from "react";
import { useState, useEffect, useCallback, useRef } from "react";
import { useForm } from "react-hook-form";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage, FormDescription } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { format, addMonths } from "date-fns";
import { de } from "date-fns/locale";
import { CalendarIcon, Plus, Edit2, Trash2, ChevronDown, ChevronUp, Mail, Phone, Briefcase, User } from "lucide-react";
import { SpeechToText } from "./speech-to-text";
import { cn } from "@/lib/utils";
import { Lead, LeadPriority, LeadStatus, NotesEntry } from "./lead-card";
import { LeadHistory } from "./lead-history";
import { useToast } from "@/hooks/use-toast";
import { Checkbox } from "@/components/ui/checkbox";
import { logger } from "@/lib/logger";
import { useAuth } from "@/contexts/AuthContext";
import { updateLeadNotes } from "@/services/leadsService";
import { getUserName } from "@/services/userService";
import { Badge } from "@/components/ui/badge";
import { useFolders } from "@/hooks/use-folders";
import { FolderOpen } from "lucide-react";

const STATUS_OPTIONS = [
  { value: "old-contact", label: "Altkontakt", color: "bg-slate-500" },
  { value: "new", label: "Neu", color: "bg-blue-500" },
  { value: "not-reached", label: "Nicht erreicht", color: "bg-orange-500" },
  { value: "follow-up", label: "Wiedervorlage", color: "bg-yellow-500" },
  { value: "scheduled", label: "Terminiert", color: "bg-cyan-500" },
  { value: "in-progress", label: "In Prozess", color: "bg-purple-500" },
  { value: "won", label: "Gewonnen", color: "bg-green-500" },
  { value: "lost", label: "Verloren", color: "bg-red-500" },
  { value: "wrong-data", label: "Falsche Daten", color: "bg-gray-500" },
  { value: "delete-data", label: "Daten Löschen", color: "bg-black" }
];

const PRIORITY_OPTIONS = [
  { value: "low", label: "Niedrig", color: "bg-green-500" },
  { value: "medium", label: "Mittel", color: "bg-yellow-500" },
  { value: "high", label: "Hoch", color: "bg-red-500" }
];

const WIEDERVORLAGE_OPTIONS = [
  { months: 3, label: "3 Monate" },
  { months: 6, label: "6 Monate" },
  { months: 9, label: "9 Monate" },
  { months: 12, label: "12 Monate" }
];

const TIME_OPTIONS = [
  { value: "08:00", label: "08:00" },
  { value: "09:00", label: "09:00" },
  { value: "10:00", label: "10:00" },
  { value: "11:00", label: "11:00" },
  { value: "12:00", label: "12:00" },
  { value: "13:00", label: "13:00" },
  { value: "14:00", label: "14:00" },
  { value: "15:00", label: "15:00" },
  { value: "16:00", label: "16:00" },
  { value: "17:00", label: "17:00" },
  { value: "18:00", label: "18:00" },
  { value: "19:00", label: "19:00" }
];

const ensureValidDate = (date: Date | string | undefined | object): Date | undefined => {
  if (!date) return undefined;
  if (typeof date === 'string' && date === "") return undefined;

  try {
    let dateObj: Date;

    if (typeof date === 'string') {
      // ISO string
      dateObj = new Date(date);
    } else if (date instanceof Date) {
      // Already a Date object
      dateObj = date;
    } else if (typeof date === 'object') {
      // Handle Firestore Timestamp or similar objects
      const dateAny = date as any;

      // Firestore Timestamp has toDate() method
      if (typeof dateAny.toDate === 'function') {
        dateObj = dateAny.toDate();
      }
      // Handle __time__ format from serialized Firestore Timestamp
      else if (dateAny.__time__) {
        dateObj = new Date(dateAny.__time__);
      }
      // Handle seconds/nanoseconds format from Firestore Timestamp
      else if (dateAny.seconds !== undefined) {
        dateObj = new Date(dateAny.seconds * 1000);
      }
      // Empty object {} or unknown format - return undefined
      else {
        return undefined;
      }
    } else {
      return undefined;
    }

    return isNaN(dateObj.getTime()) ? undefined : dateObj;
  } catch (error) {
    logger.error("Invalid date:", error);
    return undefined;
  }
};

const combineDateAndTime = (date: Date | undefined, timeString: string | undefined): Date | undefined => {
  if (!date || !timeString) return date;

  try {
    const [hours, minutes] = timeString.split(':').map(Number);
    const newDate = new Date(date);
    newDate.setHours(hours);
    newDate.setMinutes(minutes);
    return newDate;
  } catch (error) {
    logger.error("Error combining date and time:", error);
    return date;
  }
};

const extractTimeFromDate = (date: Date | undefined): string => {
  if (!date) return "";

  try {
    return format(date, "HH:mm");
  } catch (error) {
    logger.error("Error extracting time:", error);
    return "";
  }
};

interface LeadFormProps {
  lead?: Lead;
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (lead: Partial<Lead>) => void;
  onFormDataChange?: (data: Partial<Lead>) => void;
  isNew?: boolean;
  isDrawer?: boolean;
  onLeadUpdate?: NoteUpdateHandler;
  noteDraft: NoteDraft;
  onNoteDraftChange: Dispatch<SetStateAction<NoteDraft>>;
}

const DateTimePicker = ({
  value,
  onChange,
  disabled
}: {
  value?: string | Date,
  onChange: (date: string) => void,
  disabled?: boolean
}) => {
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(() => {
    if (!value) return undefined;
    try {
      const date = typeof value === 'string' ? new Date(value) : value;
      return isNaN(date.getTime()) ? undefined : date;
    } catch {
      return undefined;
    }
  });

  const [selectedTime, setSelectedTime] = useState<string>(() => {
    if (!value) return "09:00";
    try {
      const date = typeof value === 'string' ? new Date(value) : value;
      return isNaN(date.getTime()) ? "09:00" : format(date, "HH:mm");
    } catch {
      return "09:00";
    }
  });

  const handleDateSelect = (date: Date | undefined) => {
    if (date) {
      try {
        const [hours, minutes] = selectedTime.split(':').map(Number);
        const newDate = new Date(date);
        newDate.setHours(hours, minutes);
        setSelectedDate(newDate);
        onChange(newDate.toISOString());
      } catch (error) {
        logger.error("Error setting date:", error);
      }
    } else {
      setSelectedDate(undefined);
      onChange("");
    }
  };

  const handleTimeSelect = (time: string) => {
    try {
      if (selectedDate) {
        const [hours, minutes] = time.split(':').map(Number);
        const newDate = new Date(selectedDate);
        newDate.setHours(hours, minutes);
        setSelectedTime(time);
        onChange(newDate.toISOString());
      } else {
        setSelectedTime(time);
      }
    } catch (error) {
      logger.error("Error setting time:", error);
    }
  };

  const displayValue = (() => {
    if (!value) return "Datum & Zeit auswählen";
    try {
      const date = typeof value === 'string' ? new Date(value) : value;
      return isNaN(date.getTime())
        ? "Datum & Zeit auswählen"
        : format(date, "dd.MM.yyyy HH:mm", { locale: de });
    } catch {
      return "Datum & Zeit auswählen";
    }
  })();

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn(
            "w-full pl-3 text-left font-normal h-8 text-sm",
            !value && "text-muted-foreground"
          )}
          disabled={disabled}
        >
          <CalendarIcon className="mr-2 h-3 w-3" />
          <span className="text-sm">{displayValue}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <div className="p-3">
          <Calendar
            mode="single"
            selected={selectedDate}
            onSelect={handleDateSelect}
            initialFocus
          />
          <div className="mt-3 border-t pt-3">
            <div className="grid grid-cols-3 gap-2">
              {TIME_OPTIONS.map((option) => (
                <Button
                  key={option.value}
                  variant={selectedTime === option.value ? "default" : "outline"}
                  className="h-8"
                  onClick={() => handleTimeSelect(option.value)}
                >
                  {option.label}
                </Button>
              ))}
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export function LeadForm({ lead, isOpen, onClose, onSubmit, onFormDataChange, isNew = false, isDrawer = false, onLeadUpdate, noteDraft, onNoteDraftChange }: LeadFormProps) {
  const { toast } = useToast();
  const [nextContactTime, setNextContactTime] = useState<string>("");
  const [notesHistory, setNotesHistory] = useState<NotesEntry[]>([]);
  const [isNotesExpanded, setIsNotesExpanded] = useState(false);
  const [isActivityExpanded, setIsActivityExpanded] = useState(false);
  const { currentUser } = useAuth();
  const { folders } = useFolders();

  const initializedLeadId = useRef<string>();

  const sanitizedLead = lead ? {
    ...lead,
    nextContactDate: ensureValidDate(lead.nextContactDate),
  } : undefined;

  const form = useForm({
    defaultValues: sanitizedLead || {
      firstName: "",
      lastName: "",
      company: "",
      position: "",
      email: "",
      phone: "",
      status: "new" as LeadStatus,
      priority: "medium" as LeadPriority,
      notes: "",
      attachment: "",
      nextContactDate: undefined,
      dataProtectionConsent: false,
    }
  });

  useEffect(() => {
    if (lead) {
      const cleanedLead = {
        ...lead,
        nextContactDate: ensureValidDate(lead.nextContactDate),
      };
      // Note updates must not reset unsaved status/contact edits.
      if (initializedLeadId.current !== lead.id || !isOpen) {
        form.reset(cleanedLead);
        initializedLeadId.current = lead.id;

        if (cleanedLead.nextContactDate) {
          setNextContactTime(extractTimeFromDate(cleanedLead.nextContactDate));
        } else {
          setNextContactTime("");
        }
      }

      setNotesHistory(getLeadNotes(cleanedLead));
    }
  }, [lead, form, isOpen]);

  // Track form changes and send to parent
  useEffect(() => {
    if (!onFormDataChange) return;

    const subscription = form.watch((value) => {
      // Only copy defined values
      const sanitizedData: Partial<Lead> = {};
      Object.entries(value as Partial<Lead>).forEach(([key, val]) => {
        if (val !== undefined && val !== null) {
          (sanitizedData as any)[key] = val;
        }
      });

      if (value.nextContactDate || nextContactTime) {
        let nextContactDate = value.nextContactDate;

        if (nextContactDate && nextContactTime) {
          nextContactDate = combineDateAndTime(nextContactDate, nextContactTime);
        }

        if (nextContactDate) {
          sanitizedData.nextContactDate = nextContactDate;
        } else {
          sanitizedData.nextContactDate = "";
        }
      } else {
        sanitizedData.nextContactDate = "";
      }

      sanitizedData.position = value.position || "";
      sanitizedData.company = value.company || "";

      if (!isNew) delete sanitizedData.notes;
      delete sanitizedData.notesHistory;
      delete sanitizedData.history;
      onFormDataChange(sanitizedData);
    });

    return () => subscription.unsubscribe();
  }, [form, nextContactTime, onFormDataChange, isNew]);

  const handleSubmit = form.handleSubmit((data) => {
    try {
      const sanitizedData: Partial<Lead> = { ...data };

      if (data.nextContactDate || nextContactTime) {
        let nextContactDate = data.nextContactDate;

        if (nextContactDate && nextContactTime) {
          nextContactDate = combineDateAndTime(nextContactDate, nextContactTime);
        }

        if (nextContactDate) {
          sanitizedData.nextContactDate = nextContactDate;
        } else {
          sanitizedData.nextContactDate = "";
        }
      } else {
        sanitizedData.nextContactDate = "";
      }

      sanitizedData.position = data.position || "";
      sanitizedData.company = data.company || "";

      if (!isNew) delete sanitizedData.notes;
      delete sanitizedData.notesHistory;
      delete sanitizedData.history;
      onSubmit(sanitizedData);
      toast({
        title: isNew ? "Lead erstellt" : "Lead aktualisiert",
        description: isNew ? "Der Lead wurde erfolgreich erstellt." : "Der Lead wurde erfolgreich aktualisiert.",
      });
      onClose();
    } catch (error) {
      logger.error("Error submitting form:", error);
      toast({
        title: "Fehler",
        description: "Beim Speichern des Leads ist ein Fehler aufgetreten.",
        variant: "destructive",
      });
    }
  });

  const handleWiedervorlage = (months: number) => {
    const newDate = addMonths(new Date(), months);
    newDate.setHours(9, 0, 0, 0);
    form.setValue("nextContactDate", newDate);
    form.setValue("status", "follow-up");

    toast({
      title: "Wiedervorlage gesetzt",
      description: `Lead wird in ${months} Monaten wiedervorgelegt (${format(newDate, "dd.MM.yyyy", { locale: de })})`,
    });
  };

  const handleStatusChange = (newStatus: string, onChange: (value: string) => void) => {
    onChange(newStatus);
  };

  if (isDrawer) {
    return (
      <Form {...form}>
        <form onSubmit={handleSubmit} className="space-y-6 px-6 py-4">
          {/* Header with Avatar and Basic Info */}
          <div className="flex items-start gap-4 pb-6 border-b mb-2">
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-primary to-primary/60 flex items-center justify-center text-white text-xl font-semibold shadow-md">
              {lead?.firstName?.[0]}{lead?.lastName?.[0]}
            </div>
            <div className="flex-1">
              <h2 className="text-2xl font-bold text-foreground mb-2">
                {lead?.firstName} {lead?.lastName}
              </h2>
              <div className="flex flex-col gap-1 text-sm text-muted-foreground">
                {lead?.email && (
                  <div className="flex items-center gap-2">
                    <Mail className="h-4 w-4" />
                    <span>{lead.email}</span>
                  </div>
                )}
                {lead?.phone && (
                  <div className="flex items-center gap-2">
                    <Phone className="h-4 w-4" />
                    <span>{lead.phone}</span>
                  </div>
                )}
              </div>
            </div>
            {lead?.phone && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="gap-2 mt-2"
                onClick={() => window.location.href = `tel:${lead.phone}`}
              >
                <Phone className="h-4 w-4" />
                Anrufen
              </Button>
            )}
          </div>

          {/* Status Buttons */}
          <div>
            <FormLabel className="text-sm font-medium mb-3 block">Status</FormLabel>
            <FormField
              control={form.control}
              name="status"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <div className="flex flex-wrap gap-2">
                      {STATUS_OPTIONS.map((option) => (
                        <Button
                          key={option.value}
                          type="button"
                          variant={field.value === option.value ? "default" : "outline"}
                          size="sm"
                          className={cn(
                            "transition-all",
                            field.value === option.value && option.color + " text-white hover:opacity-90"
                          )}
                          onClick={() => handleStatusChange(option.value, field.onChange)}
                        >
                          {option.label}
                        </Button>
                      ))}
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          {/* Form Fields */}
          <div className="grid grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="firstName"
              render={({ field }) => (
                <div>
                  <div className="text-xs text-muted-foreground mb-1">Vorname*</div>
                  <Input
                    placeholder="Vorname eingeben"
                    {...field}
                    className="h-8 text-sm"
                  />
                </div>
              )}
            />

            <FormField
              control={form.control}
              name="lastName"
              render={({ field }) => (
                <div>
                  <div className="text-xs text-muted-foreground mb-1">Nachname*</div>
                  <Input
                    placeholder="Nachname eingeben"
                    {...field}
                    className="h-8 text-sm"
                  />
                </div>
              )}
            />

            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <div>
                  <div className="text-xs text-muted-foreground mb-1">E-Mail</div>
                  <Input
                    type="email"
                    placeholder="email@beispiel.de"
                    {...field}
                    className="h-8 text-sm"
                  />
                </div>
              )}
            />

            <FormField
              control={form.control}
              name="phone"
              render={({ field }) => (
                <div>
                  <div className="text-xs text-muted-foreground mb-1">Telefon*</div>
                  <Input
                    type="tel"
                    placeholder="+49 123 456789"
                    {...field}
                    className="h-8 text-sm"
                  />
                </div>
              )}
            />

            <FormField
              control={form.control}
              name="attachment"
              render={({ field }) => (
                <div>
                  <div className="text-xs text-muted-foreground mb-1">Aufhänger</div>
                  <Input
                    placeholder="z.B. Messe Köln"
                    {...field}
                    className="h-8 text-sm"
                  />
                </div>
              )}
            />

            <FormField
              control={form.control}
              name="priority"
              render={({ field }) => (
                <div>
                  <div className="text-xs text-muted-foreground mb-1">Priorität</div>
                  <Select
                    onValueChange={field.onChange}
                    defaultValue={field.value || "medium"}
                    value={field.value || "medium"}
                  >
                    <SelectTrigger className="h-8 text-sm">
                      <SelectValue placeholder="Priorität auswählen" />
                    </SelectTrigger>
                    <SelectContent>
                      {PRIORITY_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          <div className="flex items-center gap-2">
                            <div className={cn("w-2 h-2 rounded-full", option.color)} />
                            {option.label}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            />

            <FormField
              control={form.control}
              name="folderId"
              render={({ field }) => (
                <div>
                  <div className="text-xs text-muted-foreground mb-1">Ordner</div>
                  <Select
                    onValueChange={(value) => field.onChange(value === "none" ? null : value)}
                    value={field.value || "none"}
                  >
                    <SelectTrigger className="h-8 text-sm">
                      <FolderOpen className="h-4 w-4 mr-2" />
                      <SelectValue placeholder="Kein Ordner" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Kein Ordner</SelectItem>
                      {folders.map((folder) => (
                        <SelectItem key={folder.id} value={folder.id}>
                          <div className="flex items-center gap-2">
                            <div
                              className="w-2 h-2 rounded-full"
                              style={{ backgroundColor: folder.color || "#3B82F6" }}
                            />
                            {folder.name}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            />

            <FormField
              control={form.control}
              name="position"
              render={({ field }) => (
                <div>
                  <div className="text-xs text-muted-foreground mb-1">Berufsbezeichnung</div>
                  <Input
                    placeholder="Position"
                    {...field}
                    className="h-8 text-sm"
                  />
                </div>
              )}
            />
          </div>

          {/* Next Contact Date & Wiedervorlage */}
          <div className="grid grid-cols-2 gap-6">
            <div>
              <div className="text-xs text-muted-foreground mb-1">Nächster Kontakt</div>
              <FormField
                control={form.control}
                name="nextContactDate"
                render={({ field }) => (
                  <DateTimePicker
                    value={field.value}
                    onChange={field.onChange}
                  />
                )}
              />
            </div>

            <div>
              <div className="text-xs text-muted-foreground mb-1">Wiedervorlage</div>
              <div className="flex gap-2 flex-wrap">
                {WIEDERVORLAGE_OPTIONS.map((option) => (
                  <Button
                    key={option.months}
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => handleWiedervorlage(option.months)}
                    className="flex-1 min-w-[60px]"
                  >
                    {option.label}
                  </Button>
                ))}
              </div>
            </div>
          </div>

          {/* Data Protection Consent */}
          <FormField
            control={form.control}
            name="dataProtectionConsent"
            render={({ field }) => (
              <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                <FormControl>
                  <Checkbox
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
                <div className="space-y-1 leading-none">
                  <FormLabel className="text-sm font-normal">
                    Datenschutzbestätigung
                  </FormLabel>
                  <FormDescription className="text-xs">
                    Ich bestätige, dass ich die Datenschutzerklärung gelesen habe.
                  </FormDescription>
                </div>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Collapsible Notes Section */}
          <div className="border rounded-lg">
            <button
              type="button"
              className="w-full px-4 py-3 flex items-center justify-between hover:bg-muted/50 transition-colors"
              onClick={() => setIsNotesExpanded(!isNotesExpanded)}
            >
              <div className="flex items-center gap-2">
                <span className="font-medium">Notizen</span>
                <Badge variant="secondary">{notesHistory.length}</Badge>
              </div>
              {isNotesExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </button>
            <div className="border-t p-4" hidden={!isNotesExpanded}>
                <NotesSection
                  lead={lead}
                  notesHistory={notesHistory}
                  setNotesHistory={setNotesHistory}
                  onLeadUpdate={onLeadUpdate}
                  noteDraft={noteDraft}
                  onNoteDraftChange={onNoteDraftChange}
                />
            </div>
          </div>

          {/* Collapsible Activity Section */}
          {!isNew && (
            <div className="border rounded-lg">
              <button
                type="button"
                className="w-full px-4 py-3 flex items-center justify-between hover:bg-muted/50 transition-colors"
                onClick={() => setIsActivityExpanded(!isActivityExpanded)}
              >
                <span className="font-medium">Historie</span>
                {isActivityExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              </button>
              {isActivityExpanded && (
                <div className="border-t p-4">
                  {lead?.history && lead.history.length > 0 ? (
                    <LeadHistory history={lead.history} />
                  ) : (
                    <div className="text-center py-4 text-muted-foreground text-sm">
                      Keine Aktivitäten vorhanden.
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </form>
      </Form>
    );
  }

  return null;
}

interface NotesSectionProps {
  lead?: Lead;
  notesHistory: NotesEntry[];
  setNotesHistory: Dispatch<SetStateAction<NotesEntry[]>>;
  onLeadUpdate?: NoteUpdateHandler;
  noteDraft: NoteDraft;
  onNoteDraftChange: Dispatch<SetStateAction<NoteDraft>>;
}

const NotesSection = ({ lead, notesHistory, setNotesHistory, onLeadUpdate, noteDraft, onNoteDraftChange }: NotesSectionProps) => {
  const { newNote, editingNote, editContent } = noteDraft;
  const pending = useRef(false);
  const setNewNote = (value: string) => onNoteDraftChange(current => ({ ...current, newNote: value }));
  const setEditingNote = (value: string | null) => onNoteDraftChange(current => ({ ...current, editingNote: value }));
  const setEditContent = (value: string) => onNoteDraftChange(current => ({ ...current, editContent: value }));
  const [userNames, setUserNames] = useState<Record<string, string>>({});
  const { currentUser } = useAuth();
  const { toast } = useToast();

  useEffect(() => {
    const fetchUserNames = async () => {
      const uniqueUserIds = [...new Set(notesHistory.map(note => note.createdBy))];
      const names: Record<string, string> = {};

      for (const userId of uniqueUserIds) {
        try {
          const name = await getUserName(userId);
          names[userId] = name;
        } catch (error) {
          logger.error("Error fetching user name for", userId, error);
          names[userId] = "Unbekannter Benutzer";
        }
      }

      setUserNames(names);
    };

    if (notesHistory.length > 0) {
      fetchUserNames();
    }
  }, [notesHistory]);

  const saveMutation = async (mutation: NoteMutation, historyEntry: import("@/components/lead-card").HistoryEntry) => {
    if (!lead?.id || pending.current || noteDraft.saving) return false;
    pending.current = true;
    onNoteDraftChange(current => ({ ...current, saving: true }));
    try {
      const success = await updateLeadNotes(lead.id, mutation, historyEntry);
      if (!success) {
        toast({ title: "Fehler", description: "Die Notiz konnte nicht gespeichert werden. Prüfen Sie die Verbindung oder eine zwischenzeitliche Änderung. Ihr Entwurf bleibt erhalten.", variant: "destructive" });
        return false;
      }
      setNotesHistory(current => mergeNoteUpdate({ ...lead, notes: "", notesHistory: current }, mutation, historyEntry).notesHistory || []);
      onLeadUpdate?.(lead.id, mutation, historyEntry);
      toast({ title: "Notiz gespeichert", description: "Die Änderung wurde gespeichert." });
      return true;
    } finally {
      pending.current = false;
      onNoteDraftChange(current => ({ ...current, saving: false }));
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    const submitted = noteDraft;
    const note: NotesEntry = {
      id: crypto.randomUUID(), content: newNote.trim(),
      timestamp: new Date().toISOString(), createdBy: currentUser?.id || "System"
    };
    if (await saveMutation({ type: "add", note }, {
      date: note.timestamp, type: "note_added", changes: [], user: note.createdBy, noteContent: note.content
    })) onNoteDraftChange(current => finishNoteSave(current, submitted, false));
  };

  const handleSpeechToText = (text: string) => {
    const currentValue = newNote || "";
    const newValue = currentValue.trim() ? `${currentValue.trim()} ${text}` : text;
    setNewNote(newValue);
  };

  const handleEditNote = (noteId: string, content: string) => {
    onNoteDraftChange(current => ({ ...current, editingNote: noteId, editContent: content, editOriginalContent: content }));
  };

  const handleSaveEdit = async (noteId: string) => {
    const originalNote = notesHistory.find(note => note.id === noteId);
    if (!editContent.trim() || !originalNote) return;
    const submitted = noteDraft;
    if (await saveMutation({ type: "edit", noteId, content: editContent.trim(), expectedContent: noteDraft.editOriginalContent }, {
      date: new Date().toISOString(), type: "note_updated",
      changes: [{ field: "noteContent", oldValue: noteDraft.editOriginalContent, newValue: editContent.trim() }],
      user: currentUser?.id || "System", noteContent: editContent.trim()
    })) onNoteDraftChange(current => finishNoteSave(current, submitted, true));
  };

  const handleDeleteNote = async (noteId: string) => {
    const note = notesHistory.find(note => note.id === noteId);
    if (!note) return;
    await saveMutation({ type: "delete", noteId, expectedContent: note.content }, {
      date: new Date().toISOString(), type: "note_deleted", changes: [],
      user: currentUser?.id || "System", noteContent: note.content
    });
  };

  const handleCancelEdit = () => {
    setEditingNote(null);
    setEditContent("");
  };

  return (
    <div className="space-y-4">
      {/* Add Note */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium">Notiz hinzufügen</span>
          <SpeechToText
            onTranscription={handleSpeechToText}
            size="sm"
          />
        </div>
        <Textarea
          value={newNote}
          onChange={(e) => setNewNote(e.target.value)}
          placeholder="Schreiben Sie hier Ihre Notiz..."
          className="min-h-[80px] text-sm"
        />
        <div className="flex justify-end">
          <Button
            type="button"
            onClick={handleAddNote}
            disabled={noteDraft.saving || !newNote.trim()}
            size="sm"
          >
            <Plus className="w-4 h-4 mr-1" />
            Notiz speichern
          </Button>
        </div>
      </div>

      {/* Notes List */}
      {notesHistory.length > 0 && (
        <div className="space-y-3 max-h-[400px] overflow-y-auto">
          {notesHistory.map((note) => (
            <div key={note.id} className="group border rounded-lg p-3 hover:bg-muted/50 transition-colors">
              {editingNote === note.id ? (
                <div className="space-y-2">
                  <Textarea
                    value={editContent}
                    onChange={(e) => setEditContent(e.target.value)}
                    className="min-h-[80px] text-sm"
                    autoFocus
                  />
                  <div className="flex gap-2 justify-end">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handleCancelEdit}
                    >
                      Abbrechen
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      onClick={() => handleSaveEdit(note.id)}
                      disabled={noteDraft.saving || !editContent.trim()}
                    >
                      Speichern
                    </Button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <span>{format(new Date(note.timestamp), "dd.MM.yyyy HH:mm", { locale: de })}</span>
                      <span>•</span>
                      <span>{userNames[note.createdBy] || "Unbekannter Benutzer"}</span>
                    </div>
                    <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0"
                        onClick={() => handleEditNote(note.id, note.content)}
                      >
                        <Edit2 className="h-3 w-3" />
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0 text-destructive"
                        disabled={noteDraft.saving}
                        onClick={() => handleDeleteNote(note.id)}
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                  <p className="text-sm whitespace-pre-wrap">{note.content}</p>
                </>
              )}
            </div>
          ))}
        </div>
      )}

      {notesHistory.length === 0 && (
        <div className="text-center py-6 text-muted-foreground text-sm">
          Noch keine Notizen vorhanden.
        </div>
      )}
    </div>
  );
};
